import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CloudUpload as UploadIcon,
  List as TransactionsIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  FileCopy as DuplicatesIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Payment as PaymentIcon,
  Work as JobsIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  AccountBalanceWallet as BalancesIcon,
  EventRepeat as UpcomingIcon,
  FactCheck as ReviewIcon,
  Rule as ChecksIcon,
} from '@mui/icons-material';
import { useUser, useStackApp } from '@stackframe/react';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { usersApi, reviewApi } from '../services/api';
import NotificationBell from './NotificationBell';
import { useUserSettings } from '../hooks/useUserSettings';

const Layout = () => {
  // The user's currency and number format; the page re-renders when they change
  const { settings: userSettings } = useUserSettings();
  const moneyKey = `${userSettings?.currency || 'INR'}-${userSettings?.numberFormat || 'indian'}`;
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showError, showSuccess } = useNotification();

  // Neon Auth hooks
  const user = useUser();
  const stackApp = useStackApp();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  // Transactions from emails waiting for review (menu badge)
  const [reviewCount, setReviewCount] = useState(0);

  // Fetch users and current user on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Checked on every page change and every minute
  useEffect(() => {
    const check = () => reviewApi.summary()
      .then(response => setReviewCount(response.data.count || 0))
      .catch(() => {});
    check();
    const timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  }, [location.pathname]);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data.users);
      setCurrentUser(response.data.currentUser);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSwitchUser = async (userId) => {
    try {
      const response = await usersApi.switch(userId);
      setCurrentUser(response.data.currentUser);
      showSuccess(response.data.message);
      
      // Refresh the page to update all data
      window.location.reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to switch user');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await stackApp.signOut();
      handleUserMenuClose();
      showSuccess('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      showError('Failed to logout');
    }
  };

  const handleAccountSettings = () => {
    handleUserMenuClose();
    // Navigate to account settings - you can implement this route if needed
    navigate('/handler/account-settings');
  };

  const navigationItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Upload', icon: <UploadIcon />, path: '/upload' },
    { text: 'Email', icon: <EmailIcon />, path: '/email' },
    { text: 'Review', icon: <ReviewIcon />, path: '/review', count: reviewCount },
    { text: 'Processing Jobs', icon: <JobsIcon />, path: '/processing-jobs' },
    { text: 'Transactions', icon: <TransactionsIcon />, path: '/transactions' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Upcoming', icon: <UpcomingIcon />, path: '/upcoming' },
    { text: 'Checks', icon: <ChecksIcon />, path: '/checks' },
    { text: 'Balances', icon: <BalancesIcon />, path: '/balances' },
    { text: 'Find Duplicates', icon: <DuplicatesIcon />, path: '/duplicates' },
    { text: 'Split & Settle', icon: <PeopleIcon />, path: '/split' },
    { text: 'Subscription', icon: <PaymentIcon />, path: '/subscription' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider' }}>
      {/* Only show toolbar on mobile for logo */}
      {isMobile && (
        <>
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="/logo192.png" 
                alt="PinePocket Logo" 
                style={{ 
                  height: '28px', 
                  marginRight: '12px',
                  objectFit: 'contain'
                }} 
              />
              <Typography variant="h6" noWrap component="div">
                PinePocket
              </Typography>
            </Box>
          </Toolbar>
          <Divider />
        </>
      )}
      <List sx={{ pt: isMobile ? 1 : 2 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                mx: 1,
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {item.count > 0 && <Chip size="small" color="primary" label={item.count} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const drawerWidth = 240;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/logo512.png" 
              alt="PinePocket Logo" 
              style={{ 
                height: '56px', 
                marginRight: '12px',
                objectFit: 'contain'
              }} 
            />
            <Typography variant="h2" noWrap component="div">
              PinePocket
            </Typography>
          </Box>

          {/* Notification Bell */}
          <NotificationBell />

          {/* Custom User Menu */}
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <Button
                onClick={handleUserMenuOpen}
                color="inherit"
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar 
                    src={user.profileImageUrl} 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: 'secondary.main',
                      fontSize: '0.875rem'
                    }}
                  >
                    {user.displayName?.charAt(0) || user.primaryEmail?.charAt(0) || 'U'}
                  </Avatar>
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'block' },
                    textAlign: 'left',
                    color: 'inherit'
                  }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.2, color: 'inherit' }}>
                      {user.displayName || 'User'}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.8,
                      color: 'inherit',
                      display: 'block',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.primaryEmail}
                    </Typography>
                  </Box>
                </Box>
              </Button>

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    minWidth: 280,
                    borderRadius: '8px',
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1.5,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {/* User Info Header */}
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {user.displayName || 'User Account'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {user.primaryEmail}
                  </Typography>
                  {user.primaryEmailVerified && (
                    <Chip
                      label="Verified"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ mt: 1, height: '20px', fontSize: '0.75rem' }}
                    />
                  )}
                </Box>

                {/* Menu Items */}
                <MenuItem onClick={handleAccountSettings}>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Account Settings" />
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Sign Out" />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              color="inherit"
              onClick={() => navigate('/login')}
              startIcon={<AccountCircleIcon />}
            >
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Desktop sidebar - permanent drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: '64px', // Start below the AppBar (standard Toolbar height)
              height: 'calc(100% - 64px)',
              borderRight: 'none', // Remove default border
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile drawer - temporary */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Toolbar />
        <Box sx={{ py: 3, px: 1.5 }}>
          <Outlet key={moneyKey} />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
